import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { useLeadsRegistryCardsVirtualScroll } from '@app/pages/Leads/LeadsRegistryPage/ui/components/LeadsRegistryCards/hooks/useLeadsRegistryCardsVirtualScroll';

import type { LeadsFunnelBoardLead } from '../../../../model/types';
import type { RefObject } from 'react';

// Fallback used only before the scroll container is measurable (no ref, no
// ResizeObserver). useLayoutEffect measures the real height synchronously
// before the first paint, so this value is never visible in a normal browser.
const FALLBACK_VIEWPORT_HEIGHT = 360;

interface UseLeadsFunnelStageVirtualCardsParams {
  leads: LeadsFunnelBoardLead[];
  // While a board drag is active the column renders the full list so dnd-kit
  // keeps every sortable node mounted (offscreen items would otherwise be
  // unmounted and break collision detection / cross-stage drops).
  isDragActive: boolean;
  cardHeight: number;
  cardGap: number;
  overscan: number;
}

interface UseLeadsFunnelStageVirtualCardsResult {
  scrollContainerRef: RefObject<HTMLDivElement>;
  visibleLeads: LeadsFunnelBoardLead[];
  topSpacerHeight: number;
  bottomSpacerHeight: number;
  hasScrollableContent: boolean;
  updateScrollTop(scrollTop: number): void;
}

export const useLeadsFunnelStageVirtualCards = ({
  leads,
  isDragActive,
  cardHeight,
  cardGap,
  overscan,
}: UseLeadsFunnelStageVirtualCardsParams): UseLeadsFunnelStageVirtualCardsResult => {
  const stride = cardHeight + cardGap;
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [viewportHeight, setViewportHeight] = useState(
    FALLBACK_VIEWPORT_HEIGHT
  );

  const { range, updateScrollTop, visibleItems } =
    useLeadsRegistryCardsVirtualScroll({
      itemHeight: stride,
      items: leads,
      overscan,
      viewportHeight,
    });

  // useLayoutEffect fires synchronously after the DOM is committed but before
  // the browser paints. The initial updateViewportHeight() call therefore runs
  // before the first visible frame, so the fallback 360px state is never
  // painted — eliminating the layout shift that would otherwise occur when the
  // ResizeObserver corrected the viewport height after the first paint.
  // The ResizeObserver handles all subsequent column height changes (e.g. when
  // the board header grows or the window is resized).
  useLayoutEffect(() => {
    const scrollContainer = scrollContainerRef.current;

    if (!scrollContainer || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const updateViewportHeight = () => {
      setViewportHeight((prev) => {
        const next = scrollContainer.clientHeight;

        return prev === next ? prev : next;
      });
    };

    updateViewportHeight();

    const resizeObserver = new ResizeObserver(updateViewportHeight);
    resizeObserver.observe(scrollContainer);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // Drag mode renders every card; spacers collapse so positions stay correct.
  const visibleLeads = isDragActive ? leads : visibleItems;
  const topSpacerHeight = isDragActive ? 0 : range.offsetTop;
  const bottomSpacerHeight = isDragActive
    ? 0
    : Math.max(
        0,
        range.totalHeight - range.offsetTop - visibleItems.length * stride
      );
  const hasScrollableContent = range.totalHeight > viewportHeight;

  const scrollTopHandler = useCallback(
    (scrollTop: number) => {
      updateScrollTop(scrollTop);
    },
    [updateScrollTop]
  );

  return useMemo(
    () => ({
      scrollContainerRef,
      visibleLeads,
      topSpacerHeight,
      bottomSpacerHeight,
      hasScrollableContent,
      updateScrollTop: scrollTopHandler,
    }),
    [
      bottomSpacerHeight,
      hasScrollableContent,
      scrollTopHandler,
      topSpacerHeight,
      visibleLeads,
    ]
  );
};
